import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { confirmReset, verifyResetCode } from "@bw/shared/firebase";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { AuthCard } from "../components/AuthCard";
import { newPasswordSchema, type NewPasswordValues } from "../schemas";

type PageState = "invalid" | "verifying" | "ready" | "success";

const CONFIRM_FALLBACK =
  "Could not update your password. Please try again, or request a new reset link.";

/** Firebase password-reset landing, retaining the existing fail-closed flow. */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const oobCode = (searchParams.get("oobCode") ?? "").trim();
  const [state, setState] = useState<PageState>(
    oobCode ? "verifying" : "invalid",
  );
  const [email, setEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [codeDead, setCodeDead] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!oobCode) return;

    let cancelled = false;
    verifyResetCode(oobCode)
      .then((accountEmail) => {
        if (!cancelled) {
          setEmail(accountEmail);
          setState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) setState("invalid");
      });

    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordValues>({
    resolver: zodResolver(newPasswordSchema),
    mode: "onSubmit",
  });

  const onSubmit = handleSubmit(async (values) => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    setFormError(null);
    setCodeDead(false);
    try {
      await confirmReset(oobCode, values.password);
      setState("success");
    } catch (error) {
      setFormError(getAuthErrorMessage(error, CONFIRM_FALLBACK));
      const code =
        error && typeof error === "object" && "code" in error
          ? (error as { code?: unknown }).code
          : undefined;
      setCodeDead(
        code === "auth/expired-action-code" ||
          code === "auth/invalid-action-code",
      );
    } finally {
      submittingRef.current = false;
    }
  });

  return (
    <AuthCard heading="Set a new password">
      {state === "invalid" ? (
        <Paper variant="outlined" sx={{ p: { xs: 2.25, sm: 3 } }}>
          <Stack spacing={2}>
            <Alert severity="warning" role="status">
              This password reset link is not valid — it may have expired or
              already been used.
            </Alert>
            <Link component={RouterLink} to="/login">
              Request a new reset link
            </Link>
          </Stack>
        </Paper>
      ) : null}

      {state === "verifying" ? (
        <Stack
          role="status"
          aria-busy="true"
          spacing={1.5}
          sx={{ alignItems: "center", py: 4 }}
        >
          <CircularProgress size={28} />
          <Typography color="text.secondary">
            Checking your reset link…
          </Typography>
        </Stack>
      ) : null}

      {state === "ready" ? (
        <Paper
          component="form"
          variant="outlined"
          onSubmit={onSubmit}
          noValidate
          sx={{ p: { xs: 2.25, sm: 3 } }}
        >
          <Stack spacing={2.25}>
            <Typography color="text.secondary" variant="body2">
              Choose a new password for{" "}
              <Box component="strong" sx={{ color: "text.primary" }}>
                {email}
              </Box>
              .
            </Typography>

            {formError ? (
              <Alert severity="error" role="alert">
                {formError}
              </Alert>
            ) : null}
            {formError && codeDead ? (
              <Link component={RouterLink} to="/login">
                Request a new reset link
              </Link>
            ) : null}

            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              {...register("password")}
            />

            <TextField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword?.message}
              {...register("confirmPassword")}
            />

            <Button
              type="submit"
              variant="contained"
              size="small"
              fullWidth
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress
                    size={16}
                    color="inherit"
                    aria-hidden="true"
                  />
                ) : undefined
              }
            >
              Update password
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {state === "success" ? (
        <Paper variant="outlined" sx={{ p: { xs: 2.25, sm: 3 } }}>
          <Stack spacing={2}>
            <Alert severity="success" role="status">
              Your password has been updated.
            </Alert>
            <Link component={RouterLink} to="/login">
              Sign in
            </Link>
          </Stack>
        </Paper>
      ) : null}
    </AuthCard>
  );
}
